import { useCallback, useEffect, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { SalesTargetList, SalesTargetListsResponse } from '../../api/types';
import { AnchoredPopover } from '../shared/AnchoredPopover';
import { countPasted } from './salesViewFilters';

const POP_CLASS = 'sv-targets-panel';

/**
 * CSE-17 leader target lists: managers, directors (cse_admin) and admins paste a new list,
 * untarget clients from one list or all, or delete one. FILTERING on a list is the Targets chip in
 * the tier row, so a rep sees no button here at all.
 */
export function SalesTargetLists({
  selected, onChange, impersonateRole, onChanged, onEditable, onLists, reloadToken = 0,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  impersonateRole?: string;
  /** Something about the lists changed; the board's Target pills and filter results are stale. */
  onChanged?: () => void;
  onEditable?: (canEdit: boolean) => void;
  /** Every list, for the Targets chip. */
  onLists?: (lists: SalesTargetList[]) => void;
  /** Bumped by the parent after it untargets a client itself, so the counts here refresh. */
  reloadToken?: number;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<SalesTargetListsResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [dropText, setDropText] = useState('');
  const [dropFrom, setDropFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await siftApiClient.getSalesTargetLists(impersonateRole);
      setData(next);
      onEditable?.(next.can_edit);
      onLists?.(next.lists);
    } catch (err) {
      console.error('Target lists load failed:', err);
    }
  }, [impersonateRole, onEditable, onLists]);

  useEffect(() => { void load(); }, [load, reloadToken]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      // Portalled, so the panel is not inside wrapRef and its own clicks would read as outside.
      if (t?.closest?.(`.${POP_CLASS}`) || wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const untarget = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await siftApiClient.untargetSalesClients(dropText, dropFrom || undefined);
      const missed = res.not_targeted.length ? ` Not targeted: ${res.not_targeted.join(', ')}` : '';
      setMessage(`Untargeted ${res.removed} clients.${missed}`);
      setDropText('');
      await load();
      onChanged?.();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage(detail || 'Could not untarget those clients.');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await siftApiClient.createSalesTargetList(reason, text);
      const missed = res.unknown_count
        ? ` ${res.unknown_count} not found: ${res.unknown.join(', ')}${res.unknown_count > res.unknown.length ? '…' : ''}`
        : '';
      setMessage(`Saved ${res.matched} clients.${missed}`);
      setReason('');
      setText('');
      await load();
      onChanged?.();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setMessage(detail || 'Could not save the list.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await siftApiClient.deleteSalesTargetList(id);
      if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
      if (dropFrom === id) setDropFrom('');
      await load();
      onChanged?.();
    } catch (err) {
      console.error('Target list delete failed:', err);
      setMessage('Could not delete the list.');
    }
  };

  const lists = data?.lists ?? [];
  if (!data?.can_edit) return null;
  const pasted = countPasted(text);

  return (
    <div className="sv-targets" ref={wrapRef}>
      <button
        type="button"
        className={`sv-map-toggle${open ? ' active' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Create, untarget or delete leader target lists. Filter on them with the Targets chip."
      >
        Manage targets
      </button>
      {open && (
        <AnchoredPopover anchorRef={wrapRef} align="right" className={POP_CLASS} role="dialog"
          ariaLabel="Target lists">
          {lists.length === 0 && <div className="sv-popout-empty">No target lists yet.</div>}
          <ul className="sv-targets-list">
            {lists.map((l) => (
              <li key={l.id}>
                <span>
                  {l.reason} <span className="sv-targets-meta">· {l.client_count} clients</span>
                </span>
                <button type="button" className="sv-targets-delete" aria-label={`Delete ${l.reason}`}
                  onClick={() => void remove(l.id)}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
          <div className="sv-targets-form">
              <input
                type="text"
                aria-label="Reason"
                placeholder="Reason, e.g. Q4 offsite push"
                maxLength={200}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <textarea
                aria-label="Account numbers"
                placeholder="Paste 5M account numbers, separated by spaces, new lines or commas"
                rows={5}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <button type="button" disabled={busy || !reason.trim() || pasted === 0} onClick={() => void save()}>
                {busy ? 'Saving…' : `Save list${pasted ? ` (${pasted})` : ''}`}
              </button>
          </div>
          {lists.length > 0 && (
            <div className="sv-targets-form">
              <textarea
                aria-label="Account numbers to untarget"
                placeholder="Untarget: paste one or more account numbers"
                rows={2}
                value={dropText}
                onChange={(e) => setDropText(e.target.value)}
              />
              <select aria-label="Untarget from" value={dropFrom} onChange={(e) => setDropFrom(e.target.value)}>
                <option value="">From every list</option>
                {lists.map((l) => <option key={l.id} value={l.id}>From {l.reason}</option>)}
              </select>
              <button type="button" disabled={busy || countPasted(dropText) === 0} onClick={() => void untarget()}>
                {`Untarget${countPasted(dropText) ? ` (${countPasted(dropText)})` : ''}`}
              </button>
            </div>
          )}
          {message && <div className="sv-targets-message" role="status">{message}</div>}
        </AnchoredPopover>
      )}
    </div>
  );
}
